import { useEffect, useState } from 'react';
import { Modal, Stack, Group, Button, TextInput, Select, Text, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { MessageCircle } from 'react-feather';
import { coachPortalApi } from '../../../services/api/coachPortal';
import type { HuddleType, CoachHuddle, MeetingPlatform } from '../../../types';
import { MEETING_PLATFORM_OPTIONS } from '../../../config/cohortConfig';
import { DateTimeField } from '../../../components/common';
import { DEFAULT_START_TIME_ON_DATE_PICK } from '../../../utils/sessionEndTime';
import { platformDefaultLink } from '../../../utils/meetingLink';

// Participant option for dropdown
export interface HuddleParticipantOption {
  user_id: string;
  participant_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface CoachMeetingLinks {
  zoom_link: string | null;
  teams_link: string | null;
  google_meet_link: string | null;
}

interface GroupOption {
  id: string;
  name: string;
}

interface AddHuddleModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cohortId: string;
  groupId: string;
  groups?: GroupOption[];
  participants: HuddleParticipantOption[];
  onGroupChange?: (groupId: string) => void;
  defaultVideoLink?: string;
  defaultMeetingPlatform?: string | null;
  coachMeetingLinks?: CoachMeetingLinks | null;
  /** If provided, modal is in edit mode with pre-populated values */
  editHuddle?: CoachHuddle | null;
}

/** Resolve a coach's meeting link for a given platform */
function resolveCoachLink(
  links: CoachMeetingLinks | null | undefined,
  platform: MeetingPlatform,
): string | null {
  if (!links) return null;
  switch (platform) {
    case 'zoom': return links.zoom_link;
    case 'teams': return links.teams_link;
    case 'google_meet': return links.google_meet_link;
    case 'in_person': return null;
    default: return null;
  }
}

// MED-697 AC9: Intro and Midpoint check-ins are system-created on cohort finalization.
// Coaches can only manually create General-type check-ins via this modal.
const HUDDLE_TYPE_OPTIONS = [
  { value: 'general', label: 'General' },
];

const DURATION_OPTIONS = [
  { value: '15', label: '15 min' },
  { value: '20', label: '20 min' },
  { value: '25', label: '25 min' },
  { value: '30', label: '30 min' },
  { value: '45', label: '45 min' },
  { value: '60', label: '60 min' },
];

const DEFAULT_TITLES: Record<HuddleType, string> = {
  intro: 'Intro 1:1',
  midpoint_checkin: 'Midpoint Check-in',
  general: '',
};

interface FormValues {
  huddle_type: HuddleType | '';
  title: string;
  participant_id: string;
  scheduledDateTime: Date | null;
  duration_minutes: string;
  meeting_type: string;
  video_link: string;
}

export function AddHuddleModal({
  opened,
  onClose,
  onSuccess,
  cohortId,
  groupId,
  groups,
  participants,
  onGroupChange,
  defaultVideoLink,
  defaultMeetingPlatform,
  coachMeetingLinks,
  editHuddle,
}: AddHuddleModalProps) {
  const [loading, setLoading] = useState(false);
  const isEditMode = !!editHuddle;

  // Compute initial video link from coach meeting links + platform
  const initialPlatform = defaultMeetingPlatform || '';
  const initialVideoLink = (() => {
    if (initialPlatform && coachMeetingLinks) {
      const link = resolveCoachLink(coachMeetingLinks, initialPlatform as MeetingPlatform);
      if (link) return link;
    }
    // MED-1360: a cohort default from a different platform is not prefilled.
    return platformDefaultLink(defaultVideoLink, initialPlatform);
  })();

  const form = useForm<FormValues>({
    initialValues: {
      huddle_type: '',
      title: '',
      participant_id: '',
      scheduledDateTime: null,
      duration_minutes: '30',
      meeting_type: initialPlatform,
      video_link: initialVideoLink,
    },
    validate: {
      huddle_type: (value) => (!value ? 'Type is required' : null),
      title: (value) => (!value ? 'Title is required' : null),
      participant_id: (value) => (!value ? 'Participant is required' : null),
      scheduledDateTime: (value) => (!value ? 'Date & time is required' : null),
      duration_minutes: (value) => (!value ? 'Duration is required' : null),
    },
  });

  // Reset form when modal opens/closes or edit huddle changes
  useEffect(() => {
    if (opened) {
      if (editHuddle) {
        form.setValues({
          huddle_type: editHuddle.huddle_type,
          title: editHuddle.title,
          participant_id: editHuddle.participant_id,
          scheduledDateTime: new Date(editHuddle.scheduled_start),
          duration_minutes: editHuddle.duration_minutes.toString(),
          meeting_type: initialPlatform,
          video_link: editHuddle.video_link || '',
        });
      } else {
        form.reset();
        form.setFieldValue('huddle_type', 'general');
        form.setFieldValue('meeting_type', initialPlatform);
        form.setFieldValue('video_link', initialVideoLink);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editHuddle, defaultVideoLink]);

  // Auto-populate title when type changes
  const handleTypeChange = (value: string | null) => {
    const huddleType = value as HuddleType | '';
    form.setFieldValue('huddle_type', huddleType);
    if (huddleType && !isEditMode) {
      const defaultTitle = DEFAULT_TITLES[huddleType as HuddleType];
      form.setFieldValue('title', defaultTitle);
    }
  };

  // Auto-populate video link when meeting type changes
  const handleMeetingTypeChange = (value: string | null) => {
    form.setFieldValue('meeting_type', value || '');
    if (value && value !== 'in_person' && coachMeetingLinks) {
      const link = resolveCoachLink(coachMeetingLinks, value as MeetingPlatform);
      if (link) {
        form.setFieldValue('video_link', link);
      } else {
        form.setFieldValue('video_link', platformDefaultLink(defaultVideoLink, value));
      }
    } else if (value === 'in_person') {
      form.setFieldValue('video_link', '');
    }
  };

  const buildScheduledStart = (): string | null => {
    const { scheduledDateTime } = form.values;
    if (!scheduledDateTime) return null;
    return scheduledDateTime.toISOString();
  };

  const handleSubmit = async (sendInvite: boolean) => {
    const validation = form.validate();
    if (validation.hasErrors) return;

    const scheduledStart = buildScheduledStart();
    if (!scheduledStart) return;

    setLoading(true);
    try {
      if (isEditMode && editHuddle) {
        await coachPortalApi.updateHuddle(editHuddle.id, {
          huddle_type: form.values.huddle_type as HuddleType,
          title: form.values.title,
          participant_id: form.values.participant_id,
          scheduled_start: scheduledStart,
          duration_minutes: parseInt(form.values.duration_minutes),
          video_link: form.values.video_link || null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        notifications.show({
          title: 'Huddle updated',
          message: 'The huddle has been updated successfully.',
          color: 'green',
        });
      } else {
        await coachPortalApi.createHuddle(cohortId, {
          group_id: groupId,
          participant_id: form.values.participant_id,
          huddle_type: form.values.huddle_type as HuddleType,
          title: form.values.title,
          scheduled_start: scheduledStart,
          duration_minutes: parseInt(form.values.duration_minutes),
          video_link: form.values.video_link || undefined,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          send_invite: sendInvite,
        });
        notifications.show({
          title: sendInvite ? 'Huddle scheduled' : 'Huddle saved',
          message: sendInvite
            ? 'Calendar invites have been sent to the participant and coach.'
            : 'The huddle has been saved without sending calendar invites.',
          color: 'green',
        });
      }
      onSuccess();
      handleClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to save huddle.',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const participantOptions = participants.map((p) => ({
    value: p.participant_id,
    label: `${p.first_name} ${p.last_name}`,
  }));

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <MessageCircle size={18} />
          <Text fw={600}>{isEditMode ? 'Edit huddle' : 'Add huddle'}</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {groups && groups.length > 0 && !isEditMode && (
          <Select
            label="Group"
            placeholder="Select group"
            data={groups.map((g) => ({ value: g.id, label: g.name }))}
            value={groupId || null}
            onChange={(value) => {
              if (value && onGroupChange) {
                onGroupChange(value);
                form.setFieldValue('participant_id', '');
              }
            }}
            required
          />
        )}

        <Select
          label="Type"
          placeholder="Select huddle type"
          data={HUDDLE_TYPE_OPTIONS}
          value={form.values.huddle_type}
          onChange={handleTypeChange}
          error={form.errors.huddle_type}
          required
        />

        <TextInput
          label="Title"
          placeholder="Enter huddle title"
          {...form.getInputProps('title')}
          required
        />

        <Select
          label="Participant"
          placeholder="Select participant"
          data={participantOptions}
          searchable
          {...form.getInputProps('participant_id')}
          required
        />

        <DateTimeField
          defaultTimeOnDatePick={DEFAULT_START_TIME_ON_DATE_PICK}
          label="Date & Time"
          value={form.values.scheduledDateTime}
          onChange={(value) => form.setFieldValue('scheduledDateTime', value)}
          error={form.errors.scheduledDateTime as string | boolean | undefined}
          minDate={new Date()}
          required
        />

        <Select
          label="Duration"
          placeholder="Select duration"
          data={DURATION_OPTIONS}
          {...form.getInputProps('duration_minutes')}
          required
        />

        <Select
          label="Meeting Type"
          placeholder="Select meeting type"
          data={MEETING_PLATFORM_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
          value={form.values.meeting_type || null}
          onChange={handleMeetingTypeChange}
          clearable
        />

        {form.values.meeting_type === 'in_person' ? (
          <Alert variant="light" color="gray">
            <Text size="sm">No video link will be added for in-person meetings.</Text>
          </Alert>
        ) : (
          <TextInput
            label="Video Conferencing Link"
            placeholder="https://..."
            {...form.getInputProps('video_link')}
          />
        )}

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>
          {isEditMode ? (
            <Button onClick={() => handleSubmit(false)} loading={loading}>
              Save Changes
            </Button>
          ) : (
            <>
              <Button
                variant="light"
                onClick={() => handleSubmit(false)}
                loading={loading}
              >
                Save Only
              </Button>
              <Button onClick={() => handleSubmit(true)} loading={loading}>
                Schedule & Send Invite
              </Button>
            </>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}

export default AddHuddleModal;
